import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { queryClient } from './lib/queryClient';
import { queryPersister, PERSIST_BUSTER, PERSIST_MAX_AGE } from './lib/queryPersister';
import RealtimeQuerySync from './components/RealtimeQuerySync';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import { ChatProvider } from './contexts/ChatContext';
import { UploadProvider } from './contexts/UploadContext';
import { NotificationProvider } from './contexts/NotificationContext';
import LoadingIndicator from './components/LoadingIndicator';
import { setLoadingHandlers } from './services/api';
import ProtectedRoute from './components/ProtectedRoute';
import ModuleProtectedRoute from './components/ModuleProtectedRoute';
import DesktopOnlyWrapper from './components/DesktopOnlyWrapper';
import Login from './components/Login';
import MainDashboard from './pages/MainDashboard';
import { ChatWidget } from './components/chat';
import NotificationToast from './components/NotificationToast';
import StockLayout from './modules/stock/StockLayout';
import StockOverview from './modules/stock/pages/StockOverview';
import ClientTracking from './modules/stock/pages/ClientTracking';
import StockEntryManagement from './components/StockEntryNewManagement';
import DemandesAchatManagement from './modules/stock/pages/DemandesAchatManagement';
import BonsCommandeManagement from './modules/stock/pages/BonsCommandeManagement';
import StockExitManagement from './components/StockExitNewManagement';
import StockReturnManagement from './components/StockReturnNewManagement';
import InventoryManagement from './components/InventoryManagement';
import ConfigurationLayout from './modules/configuration/ConfigurationLayout';
import ConfigurationOverview from './modules/configuration/pages/ConfigurationOverview';
import ConditionPaiementManagement from './components/ConditionPaiementManagement';
import ModePaiementManagement from './components/ModePaiementManagement';
import AdministrationLayout from './modules/administration/AdministrationLayout';
import AdministrationOverview from './modules/administration/pages/AdministrationOverview';
import CommercialLayout from './modules/commercial/CommercialLayout';
import CommercialOverview from './modules/commercial/CommercialOverview';
import DevisList from './modules/commercial/DevisList';
import BonLivraisonList from './modules/commercial/BonLivraisonList';
import FactureList from './modules/commercial/FactureList';
import FactureAvoirList from './modules/commercial/FactureAvoirList';
import FactureProformaList from './modules/commercial/FactureProformaList';
import CompteClientManagement from './modules/commercial/CompteClientManagement';
import FinancierLayout from './modules/financier/FinancierLayout';
import ComptoirLayout from './modules/comptoir/ComptoirLayout';
import ComptoirOffres from './modules/comptoir/pages/ComptoirOffres';
import ComptoirClients from './modules/comptoir/pages/ComptoirClients';
import ComptoirClientDetail from './modules/comptoir/pages/ComptoirClientDetail';
import ComptoirCaisse from './modules/comptoir/pages/ComptoirCaisse';
import ComptoirDocuments from './modules/comptoir/pages/ComptoirDocuments';
import ComptoirStockMatiere from './modules/comptoir/pages/ComptoirStockMatiere';
import ComptoirProduction from './modules/comptoir/pages/ComptoirProduction';
import ComptoirDashboard from './modules/comptoir/pages/ComptoirDashboard';
import ComptoirFinance from './modules/comptoir/pages/ComptoirFinance';
import ComptoirServices from './modules/comptoir/pages/ComptoirServices';
import ComptoirServiceDetail from './modules/comptoir/pages/ComptoirServiceDetail';
import FinancierOverview from './modules/financier/FinancierOverview';
import EncaissementList from './modules/financier/EncaissementList';
import DecaissementList from './modules/financier/DecaissementList';
import BeneficiaireList from './modules/financier/BeneficiaireList';
import RapprochementBancaire from './modules/financier/RapprochementBancaire';
import Tresorerie from './modules/financier/Tresorerie';
import ProductionLayout from './modules/production/ProductionLayout';
import ProductionOverview from './modules/production/ProductionOverview';
import ProjetServiceManagement from './modules/production/pages/ProjetServiceManagement';
import PlanningManagement from './modules/production/pages/PlanningManagement';
import SuiviProjetService from './modules/production/pages/SuiviProjetService';
import MonSuivi from './modules/production/pages/MonSuivi';
import HRLayout from './modules/hr/HRLayout';
import HROverview from './modules/hr/HROverview';
import EmployeeList from './modules/hr/pages/EmployeeList';
import PointageGrid from './modules/hr/pages/PointageGrid';
import AttendanceBiometric from './modules/hr/pages/AttendanceBiometric';
import UtilisateursPointeuse from './modules/hr/pages/UtilisateursPointeuse';
import CongesManagement from './modules/hr/pages/CongesManagement';
import JoursFeriesManagement from './modules/hr/pages/JoursFeriesManagement';
import AttestationTravailManagement from './modules/hr/pages/AttestationTravailManagement';
import AttestationSalaireManagement from './modules/hr/pages/AttestationSalaireManagement';
import PretsManagement from './modules/hr/pages/PretsManagement';
import AcomptesManagement from './modules/hr/pages/AcomptesManagement';
import AutorisationsAbsenceManagement from './modules/hr/pages/AutorisationsAbsenceManagement';
import UserManagement from './pages/UserManagement';
import RoleManagement from './modules/administration/pages/RoleManagement';
import ModuleManagement from './modules/administration/pages/ModuleManagement';
import MiniModuleManagement from './modules/administration/pages/MiniModuleManagement';
import AccessControlManagement from './modules/administration/pages/AccessControlManagement';
import AuditLogs from './modules/administration/pages/AuditLogs';
import NotificationSettings from './modules/administration/pages/NotificationSettings';
import CommercialVisibilityManagement from './modules/administration/pages/CommercialVisibilityManagement';
import ArticleList from './components/ArticleList';
import CategoryManagement from './components/CategoryManagement';
import UnitManagement from './components/UnitManagement';
import SupplierManagement from './components/SupplierManagement';
import ClientManagement from './components/ClientManagement';
import DemandeurManagement from './modules/configuration/pages/DemandeurManagement';
import AtelierManagement from './modules/configuration/pages/AtelierManagement';
import MachineManagement from './modules/production/pages/MachineManagement';
import DessinsManagement from './modules/production/pages/DessinsManagement';
import BureauEtudes from './modules/production/pages/BureauEtudes';
import SuiviWorkflowManagement from './modules/configuration/pages/SuiviWorkflowManagement';
import SmtpSettingsManagement from './modules/configuration/pages/SmtpSettingsManagement';
import EmailEventsManagement from './modules/configuration/pages/EmailEventsManagement';
import EmailHistoryManagement from './modules/configuration/pages/EmailHistoryManagement';
import PrimeExonereeManagement from './modules/hr/pages/PrimeExonereeManagement';
import PrimeNonExonereeManagement from './modules/hr/pages/PrimeNonExonereeManagement';
import CotisationSocialeManagement from './modules/hr/pages/CotisationSocialeManagement';
import BaseCalculPaieManagement from './modules/hr/pages/BaseCalculPaieManagement';
import STCManagement from './modules/hr/pages/STCManagement';
import { MessagerieOverview } from './modules/messagerie';
import MessagerieLayout from './modules/messagerie/MessagerieLayout';
import SessionExpired from './pages/SessionExpired';
import { Typography, Container, Box } from '@mui/material';

// Unauthorized page
const Unauthorized: React.FC = () => (
  <Container maxWidth="sm">
    <Box sx={{ mt: 8, textAlign: 'center' }}>
      <Typography variant="h4" gutterBottom>
        Accès refusé
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Vous n'avez pas la permission d'accéder à cette page.
      </Typography>
    </Box>
  </Container>
);

// Not Found page
const NotFound: React.FC = () => (
  <Container maxWidth="sm">
    <Box sx={{ mt: 8, textAlign: 'center' }}>
      <Typography variant="h4" gutterBottom>
        Page non trouvée
      </Typography>
      <Typography variant="body1" color="text.secondary">
        La page que vous cherchez n'existe pas.
      </Typography>
    </Box>
  </Container>
);

/**
 * AppContent component - sets up loading handlers and renders app routes
 * Must be inside LoadingProvider to access loading context
 */
const AppContent: React.FC = () => {
  const { isLoading, loadingMessage, incrementRequests, decrementRequests } = useLoading();

  // Set up loading handlers for API interceptors
  useEffect(() => {
    setLoadingHandlers({
      incrementRequests,
      decrementRequests,
    });
  }, [incrementRequests, decrementRequests]);

  // Note: form drafts are intentionally NOT cleared on refresh / page-leave /
  // in-app navigation — only the X / ANNULER buttons (and a successful save)
  // clear a draft, so an accidental backdrop-close survives anything.

  return (
    <>
      <LoadingIndicator open={isLoading} message={loadingMessage} />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/session-expired" element={<SessionExpired />} />
        
        {/* Main Dashboard - Entry point after login */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard" replace />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <MainDashboard />
            </ProtectedRoute>
          }
        />

        {/* Stock Management Module */}
        <Route
          path="/stock"
          element={
            <ModuleProtectedRoute moduleName="Stock">
              <StockLayout />
            </ModuleProtectedRoute>
          }
        >
          <Route index element={<StockOverview />} />
          <Route
            path="demandes-achat"
            element={
              <ModuleProtectedRoute moduleName="Stock" miniModuleName="Demandes Achat">
                <DemandesAchatManagement />
              </ModuleProtectedRoute>
            }
          />
          <Route
            path="bons-commande"
            element={
              <ModuleProtectedRoute moduleName="Stock" miniModuleName="Bons Commande">
                <BonsCommandeManagement />
              </ModuleProtectedRoute>
            }
          />
          <Route
            path="entries"
            element={
              <ModuleProtectedRoute moduleName="Stock" miniModuleName="Stock Entry">
                <StockEntryManagement />
              </ModuleProtectedRoute>
            }
          />
          <Route
            path="exits"
            element={
              <ModuleProtectedRoute moduleName="Stock" miniModuleName="Stock Exit">
                <StockExitManagement />
              </ModuleProtectedRoute>
            }
          />
          <Route
            path="returns"
            element={
              <ModuleProtectedRoute moduleName="Stock" miniModuleName="Stock Return">
                <StockReturnManagement />
              </ModuleProtectedRoute>
            }
          />
          <Route
            path="inventory"
            element={
              <ModuleProtectedRoute moduleName="Stock" miniModuleName="Inventory">
                <InventoryManagement />
              </ModuleProtectedRoute>
            }
          />
          <Route
            path="clients"
            element={
              <ModuleProtectedRoute moduleName="Stock" miniModuleName="Client Tracking">
                <ClientTracking />
              </ModuleProtectedRoute>
            }
          />
          <Route path="articles" element={<ArticleList />} />
          <Route path="suppliers" element={<SupplierManagement />} />
          <Route path="categories" element={<CategoryManagement />} />
          <Route path="units" element={<UnitManagement />} />
          <Route path="demandeurs" element={<DemandeurManagement />} />
        </Route>

        {/* Configuration Module */}
        <Route
          path="/configuration"
          element={
            <ModuleProtectedRoute moduleName="Config">
              <ConfigurationLayout />
            </ModuleProtectedRoute>
          }
        >
          <Route index element={<ConfigurationOverview />} />
          <Route path="articles" element={<ArticleList />} />
          <Route path="suppliers" element={<SupplierManagement />} />
          <Route path="clients" element={<ClientManagement />} />
          <Route path="beneficiaires" element={<BeneficiaireList />} />
          <Route path="categories" element={<CategoryManagement />} />
          <Route path="units" element={<UnitManagement />} />
          <Route path="demandeurs" element={<DemandeurManagement />} />
          <Route path="ateliers" element={<AtelierManagement />} />
          <Route path="workflow-suivi" element={<SuiviWorkflowManagement />} />
          <Route path="conditions-paiement" element={<ConditionPaiementManagement />} />
          <Route path="modes-paiement" element={<ModePaiementManagement />} />
          <Route path="smtp-settings" element={<SmtpSettingsManagement />} />
          <Route path="email-events" element={<EmailEventsManagement />} />
          <Route path="email-history" element={<EmailHistoryManagement />} />
        </Route>

        {/* Administration Module */}
        <Route
          path="/administration"
          element={
            <ModuleProtectedRoute moduleName="Admin">
              <AdministrationLayout />
            </ModuleProtectedRoute>
          }
        >
          <Route index element={<AdministrationOverview />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="roles" element={<RoleManagement />} />
          <Route path="permissions" element={<AccessControlManagement />} />
          <Route path="audit-logs" element={<AuditLogs />} />
          <Route path="notifications" element={<NotificationSettings />} />
          <Route path="commercial-visibility" element={<CommercialVisibilityManagement />} />
          {/* Developer-only routes */}
          <Route path="modules" element={<ModuleManagement />} />
          <Route path="mini-modules" element={<MiniModuleManagement />} />
        </Route>

        {/* Commercial Module */}
        <Route
          path="/commercial"
          element={
            <ModuleProtectedRoute moduleName="Commercial">
              <CommercialLayout />
            </ModuleProtectedRoute>
          }
        >
          <Route index element={<CommercialOverview />} />
          <Route path="devis" element={<DevisList />} />
          <Route path="bons-livraison" element={<BonLivraisonList />} />
          <Route path="factures" element={<FactureList />} />
          <Route path="factures-avoir" element={<FactureAvoirList />} />
          <Route path="factures-proforma" element={<FactureProformaList />} />
          <Route path="compte-client" element={<CompteClientManagement />} />
          {/* Configuration items */}
          <Route path="clients" element={<ClientManagement />} />
          <Route path="conditions-paiement" element={<ConditionPaiementManagement />} />
          <Route path="modes-paiement" element={<ModePaiementManagement />} />
        </Route>

        {/* Comptoir Module — walk-in clients and their offres commerciales */}
        <Route
          path="/comptoir"
          element={
            <ModuleProtectedRoute moduleName="Comptoir">
              <ComptoirLayout />
            </ModuleProtectedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<ComptoirDashboard />} />
          <Route
            path="finance"
            element={
              <ModuleProtectedRoute moduleName="Comptoir" miniModuleName="Services & Projets">
                <ComptoirFinance />
              </ModuleProtectedRoute>
            }
          />
          <Route
            path="offres"
            element={
              <ModuleProtectedRoute moduleName="Comptoir" miniModuleName="Offres commerciales">
                <ComptoirOffres />
              </ModuleProtectedRoute>
            }
          />
          <Route
            path="clients"
            element={
              <ModuleProtectedRoute moduleName="Comptoir" miniModuleName="Clients">
                <ComptoirClients />
              </ModuleProtectedRoute>
            }
          />
          <Route
            path="caisse"
            element={
              <ModuleProtectedRoute moduleName="Comptoir" miniModuleName="Caisse">
                <ComptoirCaisse />
              </ModuleProtectedRoute>
            }
          />
          <Route
            path="documents"
            element={
              <ModuleProtectedRoute moduleName="Comptoir" miniModuleName="Bons & Documents">
                <ComptoirDocuments />
              </ModuleProtectedRoute>
            }
          />
          <Route
            path="stock-matiere"
            element={
              <ModuleProtectedRoute moduleName="Comptoir" miniModuleName="Suivi de production">
                <ComptoirStockMatiere />
              </ModuleProtectedRoute>
            }
          />
          <Route
            path="production"
            element={
              <ModuleProtectedRoute moduleName="Comptoir" miniModuleName="Suivi de production">
                <ComptoirProduction />
              </ModuleProtectedRoute>
            }
          />
          <Route
            path="clients/:id"
            element={
              <ModuleProtectedRoute moduleName="Comptoir" miniModuleName="Clients">
                <ComptoirClientDetail />
              </ModuleProtectedRoute>
            }
          />
          <Route
            path="services"
            element={
              <ModuleProtectedRoute moduleName="Comptoir" miniModuleName="Services & Projets">
                <ComptoirServices />
              </ModuleProtectedRoute>
            }
          />
          <Route
            path="services/:id"
            element={
              <ModuleProtectedRoute moduleName="Comptoir" miniModuleName="Services & Projets">
                <ComptoirServiceDetail />
              </ModuleProtectedRoute>
            }
          />
        </Route>

        {/* Financier Module */}
        <Route
          path="/financier"
          element={
            <ModuleProtectedRoute moduleName="Financier">
              <FinancierLayout />
            </ModuleProtectedRoute>
          }
        >
          <Route index element={<FinancierOverview />} />
          <Route path="encaissements" element={<EncaissementList />} />
          <Route path="decaissements" element={<DecaissementList />} />
          <Route path="rapprochement-bancaire" element={<RapprochementBancaire />} />
          <Route path="tresorerie" element={<Tresorerie />} />
          {/* Configuration items */}
          <Route path="beneficiaires" element={<BeneficiaireList />} />
        </Route>

        {/* Production Module */}
        <Route
          path="/production"
          element={
            <ModuleProtectedRoute moduleName="Production">
              <ProductionLayout />
            </ModuleProtectedRoute>
          }
        >
          <Route index element={<ProductionOverview />} />
          <Route path="projets-services" element={<ProjetServiceManagement />} />
          <Route path="planning" element={<PlanningManagement />} />
          <Route path="suivi" element={<SuiviProjetService />} />
          <Route path="mon-suivi" element={<MonSuivi />} />
          <Route path="dessins" element={<DessinsManagement />} />
          <Route
            path="bureau-etudes"
            element={
              <ModuleProtectedRoute moduleName="Production" miniModuleName="Bureau d'études">
                <BureauEtudes />
              </ModuleProtectedRoute>
            }
          />
          {/* Configuration items */}
          <Route path="ateliers" element={<AtelierManagement />} />
          <Route path="machines" element={<MachineManagement />} />
          <Route path="workflow-suivi" element={<SuiviWorkflowManagement />} />
        </Route>

        {/* Messagerie Module */}
        <Route
          path="/messagerie"
          element={
            <ProtectedRoute>
              <MessagerieLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<MessagerieOverview />} />
          <Route path="groupes" element={<MessagerieOverview filter="groups" />} />
          <Route path="non-lus" element={<MessagerieOverview filter="unread" />} />
          <Route path="favoris" element={<MessagerieOverview filter="starred" />} />
          <Route path="archives" element={<MessagerieOverview filter="archived" />} />
          <Route path="supprimes" element={<MessagerieOverview filter="deleted" />} />
          <Route path="recherche" element={<MessagerieOverview filter="search" />} />
        </Route>

        {/* HR Module */}
        <Route
          path="/hr"
          element={
            <ModuleProtectedRoute moduleName="HR">
              <HRLayout />
            </ModuleProtectedRoute>
          }
        >
          <Route index element={<HROverview />} />
          <Route path="personnel/fiches" element={<EmployeeList />} />
          <Route path="personnel/pointage" element={<PointageGrid />} />
          <Route path="personnel/pointeuse" element={
            <ModuleProtectedRoute moduleName="HR" miniModuleName="Pointage Biométrique">
              <AttendanceBiometric />
            </ModuleProtectedRoute>
          } />
          <Route path="personnel/utilisateurs-pointeuse" element={
            <ModuleProtectedRoute moduleName="HR" miniModuleName="Utilisateurs Pointeuse">
              <UtilisateursPointeuse />
            </ModuleProtectedRoute>
          } />
          <Route path="documents/attestation-travail" element={<AttestationTravailManagement />} />
          <Route path="documents/attestation-salaire" element={<AttestationSalaireManagement />} />
          <Route path="documents/demande-conge" element={<CongesManagement />} />
          <Route path="documents/demande-pret" element={<PretsManagement />} />
          <Route path="documents/demande-acompte" element={<AcomptesManagement />} />
          <Route path="documents/autorisation-absence" element={<AutorisationsAbsenceManagement />} />
          <Route path="configuration/jours-feries" element={<JoursFeriesManagement />} />
          <Route path="configuration/primes-exonerees" element={<PrimeExonereeManagement />} />
          <Route path="configuration/primes-non-exonerees" element={<PrimeNonExonereeManagement />} />
          <Route path="configuration/cotisations-sociales" element={<CotisationSocialeManagement />} />
          <Route path="paie/base-calcul" element={<BaseCalculPaieManagement />} />
          <Route path="paie/stc" element={<STCManagement />} />
        </Route>

        <Route
          path="/audit"
          element={
            <ProtectedRoute>
              <Container maxWidth="sm">
                <Box sx={{ mt: 8, textAlign: 'center' }}>
                  <Typography variant="h4" gutterBottom>
                    Module Audit
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Ce module sera disponible prochainement.
                  </Typography>
                </Box>
              </Container>
            </ProtectedRoute>
          }
        />
        
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};

/**
 * Main App component
 * Wraps the application with LoadingProvider, AuthProvider, DesktopOnlyWrapper, PermissionProvider, and ChatProvider
 */
function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: queryPersister, maxAge: PERSIST_MAX_AGE, buster: PERSIST_BUSTER }}
    >
      <LoadingProvider>
        <AuthProvider>
          {/*
            The mobile gate sits INSIDE AuthProvider on purpose: access on small
            screens is role-based (Developer only), so it needs `user`. Keeping
            it above PermissionProvider means a blocked visitor never mounts the
            permission bootstrap, the chat socket or the realtime sync.
          */}
          <DesktopOnlyWrapper>
            <PermissionProvider>
              <NotificationProvider>
                <UploadProvider>
                  <ChatProvider>
                    <RealtimeQuerySync />
                    <AppContent />
                    <ChatWidget />
                    <NotificationToast />
                  </ChatProvider>
                </UploadProvider>
              </NotificationProvider>
            </PermissionProvider>
          </DesktopOnlyWrapper>
        </AuthProvider>
      </LoadingProvider>
    </PersistQueryClientProvider>
  );
}

export default App;